import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  EVENT_FORMATS,
  formatDate,
  formatDateTime,
  isoDay,
  type EventDto,
  type EventFormat,
  type EventPresentationDto,
  type GenerationDto,
} from '@pb/shared';
import {
  deleteEvent,
  generateEvent,
  getEvent,
  markEventHeld,
  setEventPresentations,
  updateEvent,
  type EventFields,
} from '../api/events';
import { regenerate } from '../api/generations';
import { listPresentations } from '../api/presentations';
import { TypedConfirmDialog } from '../components/ConfirmDialogs';
import { DownloadGeneration, GenerationResult, Warnings, generationMeta } from '../components/Generations';
import { HistoryList } from '../components/History';
import { Tabs } from '../components/Nav';
import { ReorderList } from '../components/ReorderList';
import { DataView, ErrorAlert, Loading } from '../components/States';
import { useDetailCrumb, useProject, useProjectOwnerGuard } from '../lib/project';
import { sameIds } from '../lib/reorder';
import { pluralize } from '../lib/text';
import { useResource } from '../lib/useResource';
import { formatLabel } from './EventsPage';

function fieldsOf(e: EventDto): EventFields {
  return {
    title: e.title,
    date: e.date,
    start_time: e.start_time,
    format: e.format,
    location: e.location,
    audience: e.audience,
    attendees: e.attendees,
    notes: e.notes,
  };
}

function changedFields(before: EventFields, after: EventFields): Partial<EventFields> {
  const diff: Partial<EventFields> = {};
  if (after.title.trim() !== before.title) diff.title = after.title.trim();
  if (after.date !== before.date) diff.date = after.date;
  if ((after.start_time || null) !== (before.start_time || null)) diff.start_time = after.start_time || null;
  if (after.format !== before.format) diff.format = after.format;
  if (after.location !== before.location) diff.location = after.location;
  if (after.audience !== before.audience) diff.audience = after.audience;
  if (after.notes !== before.notes) diff.notes = after.notes;
  if (JSON.stringify(after.attendees) !== JSON.stringify(before.attendees)) diff.attendees = after.attendees;
  return diff;
}

function DetailsForm({ event, onSaved }: { event: EventDto; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState<EventFields>(() => fieldsOf(event));
  const [attendee, setAttendee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const base = fieldsOf(event);
  const diff = changedFields(base, form);
  const dirty = Object.keys(diff).length > 0;

  useEffect(() => {
    if (!dirty) setForm(fieldsOf(event));
  }, [event]);

  const set = <K extends keyof EventFields>(key: K, value: EventFields[K]) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const addAttendee = () => {
    const name = attendee.trim();
    if (!name) return;
    set('attendees', [...form.attendees, name]);
    setAttendee('');
  };

  const save = async () => {
    if (!form.title.trim() || !form.date) return;
    setBusy(true);
    setError(null);
    try {
      await updateEvent(event.id, diff);
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" aria-label="Event details">
      <div className="panel-title">
        <h3>Details</h3>
        {event.status === 'held' ? <span className="small muted">Still editable after the event; every change is recorded.</span> : null}
      </div>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="field span-2">
          <label htmlFor="evd-title">Title</label>
          <input id="evd-title" className="input" value={form.title} maxLength={200} onChange={(e) => set('title', e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="evd-date">Date</label>
          <input id="evd-date" type="date" className="input" value={form.date} onChange={(e) => set('date', e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="evd-time">Start time</label>
          <input
            id="evd-time"
            type="time"
            className="input"
            value={form.start_time ?? ''}
            onChange={(e) => set('start_time', e.target.value || null)}
          />
        </div>
        <div className="field">
          <label htmlFor="evd-format">Format</label>
          <select id="evd-format" className="select" value={form.format} onChange={(e) => set('format', e.target.value as EventFormat)}>
            {EVENT_FORMATS.map((f) => (
              <option key={f} value={f}>
                {formatLabel(f)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="evd-location">Location</label>
          <input id="evd-location" className="input" value={form.location} maxLength={500} onChange={(e) => set('location', e.target.value)} />
        </div>
        <div className="field span-2">
          <label htmlFor="evd-audience">Audience</label>
          <input id="evd-audience" className="input" value={form.audience} maxLength={1000} onChange={(e) => set('audience', e.target.value)} />
        </div>
        <div className="field span-2">
          <span className="field-label" id="evd-attendees-label">
            Attendees
          </span>
          {form.attendees.length ? (
            <ul className="row-wrap" aria-labelledby="evd-attendees-label" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {form.attendees.map((name, i) => (
                <li key={`${name}-${i}`} className="chip" style={{ cursor: 'default' }}>
                  {name}
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon"
                    style={{ width: 20, height: 20 }}
                    onClick={() => set('attendees', form.attendees.filter((_, j) => j !== i))}
                    aria-label={`Remove attendee ${name}`}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <span className="small faint">No attendees recorded.</span>
          )}
          <div className="row">
            <input
              className="input"
              value={attendee}
              maxLength={200}
              placeholder="Name of an attendee"
              aria-label="New attendee name"
              onChange={(e) => setAttendee(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAttendee();
                }
              }}
            />
            <button type="button" className="btn btn-sm" onClick={addAttendee} disabled={!attendee.trim()}>
              Add attendee
            </button>
          </div>
        </div>
        <div className="field span-2">
          <label htmlFor="evd-notes">Notes</label>
          <textarea id="evd-notes" className="textarea" value={form.notes} maxLength={10000} onChange={(e) => set('notes', e.target.value)} />
        </div>
        <div className="span-2 row">
          <button type="submit" className="btn btn-primary" disabled={!dirty || busy || !form.title.trim() || !form.date}>
            {busy ? 'Saving…' : 'Save details'}
          </button>
          <button type="button" className="btn" disabled={!dirty || busy} onClick={() => setForm(fieldsOf(event))}>
            Discard changes
          </button>
          {saved && !dirty ? <span className="small muted">Saved.</span> : null}
        </div>
      </form>
      <ErrorAlert error={error} />
    </section>
  );
}

function SnapshotCell({
  item,
  held,
  onRegenerated,
}: {
  item: EventPresentationDto;
  held: boolean;
  onRegenerated: (g: GenerationDto) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const snap = item.snapshot;
  if (!snap) return <span className="small faint">Not generated yet</span>;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row-wrap">
        <DownloadGeneration generation={snap} label={held ? 'Download as shown' : 'Download'} />
        {held ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                onRegenerated(await regenerate(snap.id));
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Regenerating…' : 'Regenerate from manifest'}
          </button>
        ) : null}
      </div>
      <span className="xsmall muted">
        {pluralize(snap.page_count, 'page')} · {generationMeta(snap)}
      </span>
      {snap.warnings.length ? (
        <span className="badge badge-warning" title={snap.warnings.join('\n')}>
          {pluralize(snap.warnings.length, 'warning')}
        </span>
      ) : null}
      <ErrorAlert error={error} />
    </div>
  );
}

function PresentationsPanel({ event, onChanged }: { event: EventDto; onChanged: () => Promise<void> }) {
  const { project } = useProject();
  const held = event.status === 'held';
  const presentations = useResource(() => listPresentations(project.id), [project.id]);
  const [adding, setAdding] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [regenerated, setRegenerated] = useState<GenerationDto | null>(null);
  const items = event.presentations;
  const ids = items.map((p) => p.presentation_id);

  const apply = async (next: number[]) => {
    if (sameIds(next, ids)) return;
    setBusy(true);
    setError(null);
    try {
      await setEventPresentations(event.id, next);
      await onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const available = (presentations.data ?? []).filter((p) => !p.retired && !ids.includes(p.id));

  return (
    <section className="panel" aria-label="Presentations">
      <div className="panel-title">
        <h3>Presentations</h3>
        {held ? <span className="badge">Locked — the event is held</span> : null}
      </div>
      {items.length === 0 ? (
        <p className="small muted">No presentations yet. Add the presentations you will give, in order.</p>
      ) : (
        <ReorderList
          label="Presentations of this event"
          items={items}
          disabled={held || busy}
          itemKey={(p) => String(p.presentation_id)}
          itemLabel={(p) => p.name}
          onChange={(next) => void apply(next.map((p) => p.presentation_id))}
          renderItem={(p, index) => (
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <span className="small faint" style={{ width: 20 }}>
                {index + 1}
              </span>
              <div className="stack grow" style={{ gap: 4 }}>
                <div className="row-wrap">
                  <Link to={`/projects/${project.id}/presentations/${p.presentation_id}`}>
                    <strong>{p.name}</strong>
                  </Link>
                  {p.retired ? <span className="badge badge-warning">Retired</span> : null}
                </div>
                <SnapshotCell item={p} held={held} onRegenerated={setRegenerated} />
              </div>
            </div>
          )}
          renderActions={
            held
              ? undefined
              : (p) => (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || items.length <= 1}
                    title={items.length <= 1 ? 'An event keeps at least one presentation' : undefined}
                    onClick={() => void apply(ids.filter((x) => x !== p.presentation_id))}
                    aria-label={`Remove ${p.name} from the event`}
                  >
                    Remove
                  </button>
                )
          }
        />
      )}
      {!held ? (
        <div className="row" style={{ marginTop: 12 }}>
          <label htmlFor="event-add-presentation" className="visually-hidden">
            Presentation to add
          </label>
          <select
            id="event-add-presentation"
            className="select"
            value={adding}
            disabled={busy || !presentations.data}
            onChange={(e) => setAdding(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">
              {presentations.data && available.length === 0 ? 'No other presentations available' : 'Choose a presentation to add…'}
            </option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            disabled={busy || adding === ''}
            onClick={() => {
              if (adding === '') return;
              void apply([...ids, adding]).then(() => setAdding(''));
            }}
          >
            Add
          </button>
        </div>
      ) : null}
      <div className="stack" style={{ marginTop: 12 }}>
        {regenerated ? (
          <>
            <div className="small muted">Regenerated from the frozen manifest:</div>
            <GenerationResult generation={regenerated} />
          </>
        ) : null}
        {presentations.error ? <ErrorAlert error={presentations.error} /> : null}
        <ErrorAlert error={error} />
      </div>
    </section>
  );
}

type EventTab = 'event' | 'history';

export function EventPage() {
  const { eventId } = useParams();
  const id = Number(eventId);
  const { project } = useProject();
  const navigate = useNavigate();
  const event = useResource(() => getEvent(id), [id]);
  const mismatched = useProjectOwnerGuard(event.data?.project_id);
  const [tab, setTab] = useState<EventTab>('event');
  const [dialog, setDialog] = useState<'held' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [generatedWarnings, setGeneratedWarnings] = useState<string[] | null>(null);
  useDetailCrumb(event.data?.title);

  const generate = async () => {
    setBusy(true);
    setActionError(null);
    setGeneratedWarnings(null);
    try {
      await generateEvent(id);
      const fresh = await getEvent(id);
      event.setData(fresh);
      setGeneratedWarnings(fresh.presentations.flatMap((p) => (p.snapshot?.warnings ?? []).map((w) => `${p.name}: ${w}`)));
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DataView resource={event} what="event" loadingLabel="Loading event…">
      {(e) => {
        if (mismatched) return <Loading label="Loading event…" />;
        const held = e.status === 'held';
        const past = e.date < isoDay(new Date());
        return (
          <div className="section">
            <div className="page-header">
              <div className="page-title">
                <h2>{e.title}</h2>
                <div className="row-wrap">
                  <span className="badge">{formatDate(e.date)}{e.start_time ? `, ${e.start_time}` : ''}</span>
                  <span className="badge">{formatLabel(e.format)}</span>
                  {held ? (
                    <span className="badge badge-success">Held</span>
                  ) : e.not_marked_held || past ? (
                    <span className="badge badge-warning">Not marked held</span>
                  ) : (
                    <span className="badge badge-accent">Planned</span>
                  )}
                  {e.location ? <span className="small muted">{e.location}</span> : null}
                </div>
                {held ? (
                  <p className="small muted">
                    Marked held {e.held_at ? formatDateTime(e.held_at) : ''}
                    {e.held_by ? ` by ${e.held_by}` : ''}. The files below are frozen exactly as shown.
                  </p>
                ) : null}
              </div>
              {!held ? (
                <div className="page-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void generate()}
                    disabled={busy || e.presentations.length === 0}
                  >
                    {busy ? 'Generating…' : 'Generate'}
                  </button>
                  <button type="button" className="btn" onClick={() => setDialog('held')} disabled={busy}>
                    Mark as held
                  </button>
                  <button type="button" className="btn" onClick={() => setDialog('delete')} disabled={busy}>
                    Delete
                  </button>
                </div>
              ) : null}
            </div>
            <ErrorAlert error={actionError} />
            {generatedWarnings !== null ? (
              generatedWarnings.length ? (
                <Warnings warnings={generatedWarnings} title="Generated, with warnings" />
              ) : (
                <div className="alert alert-success" role="status">
                  Generated {pluralize(e.presentations.length, 'presentation')} for this event. Download them below.
                </div>
              )
            ) : null}
            <Tabs
              label="Event views"
              active={tab}
              onChange={setTab}
              tabs={[
                { key: 'event', label: 'Event' },
                { key: 'history', label: 'History' },
              ]}
            />
            {tab === 'event' ? (
              <div className="two-col-even">
                <DetailsForm event={e} onSaved={event.refresh} />
                <PresentationsPanel event={e} onChanged={event.refresh} />
              </div>
            ) : (
              <HistoryList target="event" id={e.id} />
            )}
            {dialog === 'held' ? (
              <TypedConfirmDialog
                title="Mark event as held"
                name={e.title}
                what="event title"
                actionLabel="Mark as held"
                danger={false}
                onClose={() => setDialog(null)}
                onConfirm={async (confirm) => {
                  await markEventHeld(e.id, confirm);
                  setDialog(null);
                  await event.refresh();
                }}
              >
                <p>
                  Every presentation without a snapshot is generated now. Then the snapshot files are frozen for good,
                  the list of presentations is locked and the event can no longer be deleted. There is no undo.
                </p>
                <p className="small muted">Title, date, time, format, location, audience, attendees and notes stay editable.</p>
              </TypedConfirmDialog>
            ) : null}
            {dialog === 'delete' ? (
              <TypedConfirmDialog
                title="Delete event"
                name={e.title}
                what="event title"
                actionLabel="Delete event"
                onClose={() => setDialog(null)}
                onConfirm={async (confirm) => {
                  await deleteEvent(e.id, confirm);
                  navigate(`/projects/${project.id}/events`);
                }}
              >
                <p>The planned event and its snapshots are deleted. This cannot be undone.</p>
              </TypedConfirmDialog>
            ) : null}
          </div>
        );
      }}
    </DataView>
  );
}
